import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  HardDrive,
  Download,
  Trash2,
  Plus,
  Database,
  Clock,
  Archive,
  Loader2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Calendar,
  FileArchive,
  Shield
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminBackups = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  
  // Cron states
  const [cronStatus, setCronStatus] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);
  const [showCronModal, setShowCronModal] = useState(false);
  const [cronSchedule, setCronSchedule] = useState('0 2 * * *');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  
  const [description, setDescription] = useState('');

  const fetchCronStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/backups/cron/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCronStatus(response.data);
    } catch (error) {
      console.error('Feil ved henting av cron-status:', error);
    }
  }, [token]);

  const toggleCron = async (enable) => {
    setCronLoading(true);
    try {
      if (enable) {
        await axios.post(`${API}/backups/cron/enable?schedule=${encodeURIComponent(cronSchedule)}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: 'Aktivert',
          description: 'Automatisk backup er nå aktivert',
        });
      } else {
        await axios.post(`${API}/backups/cron/disable`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: 'Deaktivert',
          description: 'Automatisk backup er deaktivert',
        });
      }
      await fetchCronStatus();
      setShowCronModal(false);
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke oppdatere innstillinger',
        variant: 'destructive'
      });
    } finally {
      setCronLoading(false);
    }
  };

  const testCronBackup = async () => {
    setCronLoading(true);
    try {
      await axios.post(`${API}/backups/cron/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: 'Test fullført',
        description: 'Test-backup ble opprettet',
      });
      await fetchBackups();
      await fetchCronStatus();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke opprette test-backup',
        variant: 'destructive'
      });
    } finally {
      setCronLoading(false);
    }
  };

  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const [backupsRes, statsRes] = await Promise.all([
        axios.get(`${API}/backups`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/backups/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setBackups(backupsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Feil ved henting av backups:', error);
      toast({
        title: 'Feil',
        description: 'Kunne ikke hente backups',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    fetchBackups();
    fetchCronStatus();
  }, [fetchBackups, fetchCronStatus]);

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/backups`,
        null,
        {
          params: { description },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast({
        title: 'Backup opprettet! ✅',
        description: `${response.data.collections_count} samlinger sikkerhetskopiert (${response.data.size_formatted})`
      });
      
      setCreateModalOpen(false);
      setDescription('');
      fetchBackups();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke opprette backup',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (backup) => {
    setDownloading(backup.id);
    try {
      const response = await axios.get(
        `${API}/backups/${backup.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Nedlasting startet',
        description: backup.filename
      });
    } catch (error) {
      toast({
        title: 'Feil',
        description: 'Kunne ikke laste ned backup',
        variant: 'destructive'
      });
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedBackup) return;
    
    try {
      await axios.delete(`${API}/backups/${selectedBackup.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Backup slettet',
        description: selectedBackup.name
      });
      
      setDeleteModalOpen(false);
      setSelectedBackup(null);
      fetchBackups();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke slette backup',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Ukjent';
    return new Date(dateString).toLocaleString('no-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays} dag${diffDays > 1 ? 'er' : ''} siden`;
    if (diffHours > 0) return `${diffHours} time${diffHours > 1 ? 'r' : ''} siden`;
    if (diffMins > 0) return `${diffMins} minutt${diffMins > 1 ? 'er' : ''} siden`;
    return 'Nettopp';
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-600/20 rounded-xl">
                <Archive className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Totale backups</p>
                <p className="text-2xl font-bold text-white">{stats?.total_backups || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-600/20 rounded-xl">
                <HardDrive className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total størrelse</p>
                <p className="text-2xl font-bold text-white">{stats?.total_size_formatted || '0 B'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-600/20 rounded-xl">
                <Clock className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Siste backup</p>
                <p className="text-lg font-bold text-white">
                  {stats?.newest_backup ? getTimeSince(stats.newest_backup) : 'Ingen'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-900/50 to-orange-800/30 border-orange-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-600/20 rounded-xl">
                <Calendar className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Oppbevaring</p>
                <p className="text-2xl font-bold text-white">{stats?.retention_days || 30} dager</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Automatic Backup Configuration */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-400" />
              Automatisk backup
            </CardTitle>
            <div className="flex items-center gap-2">
              {cronStatus?.cron_configured ? (
                <Badge className="bg-green-600 text-white">Aktiv</Badge>
              ) : (
                <Badge variant="outline" className="text-gray-400 border-gray-600">Inaktiv</Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                <div>
                  <p className="font-medium text-white">Status</p>
                  <p className="text-sm text-gray-400">
                    {cronStatus?.cron_configured 
                      ? `Kjører ${cronStatus.cron_schedule_description || 'daglig'}` 
                      : 'Ikke konfigurert'}
                  </p>
                </div>
                <Button
                  onClick={() => setShowCronModal(true)}
                  variant="outline"
                  className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                >
                  {cronStatus?.cron_configured ? 'Endre' : 'Aktiver'}
                </Button>
              </div>
              
              {cronStatus?.cron_configured && (
                <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Automatiske backups</p>
                    <p className="text-sm text-gray-400">
                      {cronStatus.total_cron_backups || 0} backups opprettet automatisk
                    </p>
                  </div>
                  <Button
                    onClick={testCronBackup}
                    disabled={cronLoading}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                  >
                    {cronLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test nå'}
                  </Button>
                </div>
              )}
              
              {cronStatus?.last_cron_backup && (
                <div className="p-4 bg-green-900/20 border border-green-700 rounded-lg">
                  <p className="text-sm text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Siste automatiske backup: {formatDate(cronStatus.last_cron_backup.created_at)}
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <p className="font-medium text-white mb-3">Oppbevaringsinnstillinger</p>
              <div className="space-y-2 text-sm text-gray-400">
                <p>• Backups oppbevares i {cronStatus?.retention_days || 30} dager</p>
                <p>• Gamle backups slettes automatisk</p>
                <p>• Manuelt opprettede backups følger samme regler</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cron Configuration Modal */}
      <Dialog open={showCronModal} onOpenChange={setShowCronModal}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Automatisk backup</DialogTitle>
            <DialogDescription className="text-gray-400">
              Konfigurer automatisk backup av databasen
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Tidsplan (cron-format)</Label>
              <Input
                value={cronSchedule}
                onChange={(e) => setCronSchedule(e.target.value)}
                className="bg-gray-800 border-gray-600 text-white font-mono"
                placeholder="0 2 * * *"
              />
              <p className="text-xs text-gray-500">
                Format: minutt time dag måned ukedag<br/>
                Standard: 0 2 * * * = hver dag kl. 02:00
              </p>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-400">Vanlige tidsplaner:</p>
              <div className="flex flex-wrap gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setCronSchedule('0 2 * * *')}
                  className="text-xs border-gray-600 text-gray-300"
                >
                  Daglig 02:00
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setCronSchedule('0 3 * * 0')}
                  className="text-xs border-gray-600 text-gray-300"
                >
                  Ukentlig (søn)
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setCronSchedule('0 */6 * * *')}
                  className="text-xs border-gray-600 text-gray-300"
                >
                  Hver 6. time
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            {cronStatus?.cron_configured && (
              <Button
                onClick={() => toggleCron(false)}
                disabled={cronLoading}
                variant="destructive"
              >
                {cronLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Deaktiver
              </Button>
            )}
            <Button
              onClick={() => toggleCron(true)}
              disabled={cronLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {cronLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {cronStatus?.cron_configured ? 'Oppdater' : 'Aktiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-green-400" />
              Sikkerhetskopier
            </CardTitle>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={fetchBackups}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Oppdater
              </Button>
              <Button
                onClick={() => setCreateModalOpen(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="create-backup-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ny backup
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Info Box */}
          <div className="bg-blue-900/20 border border-blue-600/30 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h4 className="text-blue-400 font-medium mb-1">Om sikkerhetskopier</h4>
                <p className="text-gray-400 text-sm">
                  Backups inkluderer alt innhold, bookinger, brukere, tjenester og innstillinger. 
                  Filene lagres som komprimerte arkiver (.tar.gz) og oppbevares i {stats?.retention_days || 30} dager.
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">Ingen sikkerhetskopier ennå</p>
              <p className="text-gray-500 text-sm">Opprett din første backup for å sikre dataene dine</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup, index) => (
                <div
                  key={backup.id}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border transition-all ${
                    index === 0
                      ? 'bg-green-900/20 border-green-600/50'
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                  }`}
                  data-testid={`backup-item-${backup.id}`}
                >
                  <div className="flex items-center gap-4 mb-3 sm:mb-0">
                    <div className={`p-3 rounded-xl ${index === 0 ? 'bg-green-600/20' : 'bg-gray-700/50'}`}>
                      <FileArchive className={`w-6 h-6 ${index === 0 ? 'text-green-400' : 'text-gray-400'}`} />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-medium">{backup.name}</h3>
                        {index === 0 && (
                          <Badge className="bg-green-600 text-xs">Nyeste</Badge>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm">{backup.description}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <Badge variant="outline" className="border-gray-600 text-gray-400">
                          <HardDrive className="w-3 h-3 mr-1" />
                          {backup.size_formatted}
                        </Badge>
                        <Badge variant="outline" className="border-gray-600 text-gray-400">
                          <Database className="w-3 h-3 mr-1" />
                          {backup.collections_count} samlinger
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="text-right text-sm">
                      <p className="text-gray-500">Opprettet</p>
                      <p className="text-gray-300">{formatDate(backup.created_at)}</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
                        onClick={() => handleDownload(backup)}
                        disabled={downloading === backup.id}
                        data-testid={`download-backup-${backup.id}`}
                      >
                        {downloading === backup.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-600 text-red-400 hover:bg-red-600/20"
                        onClick={() => {
                          setSelectedBackup(backup);
                          setDeleteModalOpen(true);
                        }}
                        data-testid={`delete-backup-${backup.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Collections Info */}
          {backups.length > 0 && backups[0].collections && (
            <div className="mt-6 p-4 bg-gray-800/50 rounded-xl">
              <h4 className="text-gray-300 font-medium mb-2">Inkluderte samlinger i siste backup:</h4>
              <div className="flex flex-wrap gap-2">
                {backups[0].collections.map((collection) => (
                  <Badge key={collection} variant="outline" className="border-gray-600 text-gray-400">
                    {collection}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Backup Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Opprett ny backup
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Dette vil sikkerhetskopiere hele databasen inkludert alt innhold, bookinger og brukere.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="backup-desc" className="text-gray-300">
                Beskrivelse <span className="text-gray-500">(valgfritt)</span>
              </Label>
              <Input
                id="backup-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="F.eks. 'Før systemoppdatering'"
                className="bg-gray-800 border-gray-600 text-white"
                data-testid="backup-description-input"
              />
            </div>

            <div className="bg-green-900/20 border border-green-600/30 p-3 rounded-lg">
              <p className="text-green-400 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Backup vil inkludere alle data og kan ta noen sekunder
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateModalOpen(false)}
              className="border-gray-600 text-gray-300"
              disabled={creating}
            >
              Avbryt
            </Button>
            <Button
              onClick={handleCreateBackup}
              disabled={creating}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-create-backup"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Oppretter...
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-2" />
                  Opprett backup
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Bekreft sletting
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Er du sikker på at du vil slette denne sikkerhetskopien? Handlingen kan ikke angres.
            </DialogDescription>
          </DialogHeader>

          {selectedBackup && (
            <div className="py-4">
              <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
                <div className="p-3 bg-gray-700/50 rounded-xl">
                  <FileArchive className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{selectedBackup.name}</p>
                  <p className="text-gray-400 text-sm">
                    {selectedBackup.size_formatted} • {formatDate(selectedBackup.created_at)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-backup"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Slett backup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBackups;
